import {
  Box,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { ArticleNavigationContext } from '@bridge/navigationStore';
import { reactEvents } from '@bridge/reactEvents';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { Icons } from '@texas/components/shared/Icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ArticleBreadcrumbPickerProps {
  context: ArticleNavigationContext;
}

export function ArticleBreadcrumbPicker({
  context,
}: ArticleBreadcrumbPickerProps) {
  const { t } = useTranslation();
  const { data, loading, error, refetch } = useApiResource(
    articlesApi.getDirectoryArticles,
  );
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);
  const currentArticleRef = useRef<HTMLButtonElement>(null);
  const conceptId = context.concept?.id ?? null;

  useEffect(() => {
    refetch(conceptId, context.customer.id);
  }, [conceptId, context.customer.id, refetch]);

  const articles = useMemo(
    () =>
      (data ?? []).sort((a, b) =>
        a.itemCode.localeCompare(b.itemCode, undefined, { numeric: true }),
      ),
    [data],
  );
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  const filteredArticles = useMemo(
    () =>
      normalizedSearchTerm
        ? articles.filter(
            (article) =>
              article.itemCode
                .toLocaleLowerCase()
                .includes(normalizedSearchTerm) ||
              article.name.toLocaleLowerCase().includes(normalizedSearchTerm),
          )
        : articles,
    [articles, normalizedSearchTerm],
  );

  const openMenu = () => {
    setTimeout(() => {
      const menuList = menuListRef.current;
      const currentArticle = currentArticleRef.current;
      if (!menuList || !currentArticle) return;

      const menuRect = menuList.getBoundingClientRect();
      const articleRect = currentArticle.getBoundingClientRect();
      const offset =
        currentArticle.offsetTop -
        menuRect.top -
        menuRect.height / 2 +
        articleRect.height / 2;
      menuList.scrollTo({ top: offset });
    }, 50);
  };

  const closeMenu = () => {
    setSearchTerm('');
  };

  return (
    <Menu
      isLazy={true}
      placement="bottom-start"
      onClose={closeMenu}
      onOpen={openMenu}
    >
      <MenuButton
        as={Button}
        variant="unstyled"
        color="navigation.breadcrumbCurrent"
        display="flex"
        h="auto"
        w="fit-content"
        px={0}
        fontFamily="inherit"
        fontSize="inherit"
        fontWeight="semibold"
        lineHeight="inherit"
        rightIcon={<Icons.ChevronDown />}
        _hover={{ color: 'navigation.breadcrumbCurrent' }}
      >
        <Text flex={1} minW={0} textAlign="left" noOfLines={1}>
          {context.article.itemCode}
        </Text>
      </MenuButton>
      <Portal>
        <MenuList
          ref={menuListRef}
          w="320px"
          maxW="calc(100vw - 32px)"
          maxH="360px"
          overflowY="auto"
        >
          <Box position="sticky" top={0} zIndex={1} p={2} bg="chakra-body-bg">
            <Input
              ref={searchInputRef}
              size="sm"
              variant="navigationBreadcrumb"
              value={searchTerm}
              placeholder={t('searchArticles.searchArticles')}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </Box>
          {loading && (
            <MenuItem isDisabled={true} icon={<Spinner size="xs" />}>
              {t('general.loading')}
            </MenuItem>
          )}
          {!loading && error && (
            <MenuItem isDisabled={true}>{t('general.error')}</MenuItem>
          )}
          {!loading && !error && filteredArticles.length === 0 && (
            <MenuItem isDisabled={true}>{t('general.noData')}</MenuItem>
          )}
          {!loading &&
            !error &&
            filteredArticles.map((article) => {
              const isCurrent = article.id === context.article.id;

              return (
                <MenuItem
                  ref={isCurrent ? currentArticleRef : undefined}
                  key={article.id}
                  alignItems="flex-start"
                  flexDirection="column"
                  bg={
                    isCurrent ? 'navigation.contextActiveBackground' : undefined
                  }
                  aria-current={isCurrent ? 'page' : undefined}
                  onClick={() => {
                    if (isCurrent) return;

                    reactEvents.routingChanged.dispatch({
                      to: 'article',
                      navObj: { articleId: article.id },
                    });
                  }}
                >
                  <Text fontWeight={isCurrent ? 'bold' : 'semibold'}>
                    {article.itemCode}
                  </Text>
                  {article.name && article.name !== article.itemCode && (
                    <Text color="navigation.contextMuted" fontSize="xs">
                      {article.name}
                    </Text>
                  )}
                </MenuItem>
              );
            })}
        </MenuList>
      </Portal>
    </Menu>
  );
}
